// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import {
  MaybeLoadable,
  needsLoading,
  Loaded,
  Policy,
  Loadable,
  RolesService,
  QueueAlertAction,
  AlertType,
  BackAction,
  RoleObject,
  ScopeCategory,
  PlatformService
} from '@app/core';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { FormGroup, FormControl, FormBuilder, Validators } from '@angular/forms';
import {
  INPUT_TEXT_REQUIRED_ERROR,
  NAME_PATTERN_ERROR,
  DESCRIPTION_PATTERN_ERROR,
  NAME_PATTERN,
  DESCRIPTION_PATTERN
} from '@app/core/services/input-text-validator.service';
import { Store, State, select } from '@ngrx/store';
import { selectPolicies, LoadingPolicyAction, LoadingRolesAction } from '@app/access-management/state';
import { take, filter, map } from 'rxjs/operators';
import { SelectedRows } from '@app/shared/data-table-ajax/data-table-ajax.component';

const shouldLoadPolicies = (policies: MaybeLoadable) => needsLoading<Loaded<Policy[]>>(policies);
const hasPoliciesState = (policies: MaybeLoadable) => !needsLoading<Loaded<Policy[]>>(policies);

@Component({
  selector: 'app-role-create-new',
  templateUrl: './role-create-new.component.html',
  styleUrls: ['./role-create-new.component.scss']
})
export class RoleCreateNewComponent implements OnInit, OnDestroy {
  readonly policies$: Observable<Policy[]>;
  readonly loadablePolicies$: Observable<Loadable>;
  roleForm: FormGroup;
  selectedPolicies: Policy[] | undefined = undefined;
  selectedObjects: ScopeCategory[] | undefined = undefined;
  selectedServices: PlatformService[] | undefined = undefined;
  submitSubscription: Subscription | undefined = undefined;
  nameControl: FormControl;
  titleControl: FormControl;
  descriptionControl: FormControl;

  nameControlErrors = [INPUT_TEXT_REQUIRED_ERROR, NAME_PATTERN_ERROR];

  descriptionControlErrors = [INPUT_TEXT_REQUIRED_ERROR, DESCRIPTION_PATTERN_ERROR];

  policyColumns = [
    { name: 'Policy Name', prop: 'name', flexGrow: 1 },
    { name: 'Policy Description', prop: 'description', flexGrow: 1, cellClass: 'white-space-normal' }
  ];

  objectColumns = [{ name: 'Object Name', prop: 'name', flexGrow: 1 }];
  serviceColumns = [{ name: 'Service Name', prop: 'name', flexGrow: 1 }];

  constructor(private store: Store<any>, private formBuilder: FormBuilder, private rolesService: RolesService) {
    this.roleForm = this.buildRoleForm();
    this.nameControl = this.roleForm.get('roleName') as FormControl;
    this.titleControl = this.roleForm.get('roleTitle') as FormControl;
    this.descriptionControl = this.roleForm.get('roleDescription') as FormControl;

    this.loadablePolicies$ = this.store.pipe(select(selectPolicies));
    this.loadablePolicies$
      .pipe(
        take(1),
        filter(shouldLoadPolicies)
      )
      .subscribe(() => store.dispatch(new LoadingPolicyAction()));
    this.policies$ = this.loadablePolicies$.pipe(
      filter<Loaded<Policy[]> | Loadable>(hasPoliciesState),
      map(policyList => policyList.data)
    );
  }

  ngOnInit() {}

  ngOnDestroy() {
    if (this.submitSubscription !== undefined) {
      this.submitSubscription.unsubscribe();
    }
  }

  cancel() {
    this.goBackToRoleListPage();
  }

  submit() {
    const createRoleRequest = {
      roleName: this.roleForm.value['roleName'],
      roleTitle: this.roleForm.value['roleTitle'],
      roleDescription: this.roleForm.value['roleDescription'],
      policyIds: this.selectedPolicies !== undefined ? this.selectedPolicies.map(policy => policy.id) : [],
      role_objects: this.getRoleObjects(),
      service_role: this.getServiceRole()
    };
    this.submitSubscription = this.rolesService.createRole(createRoleRequest).subscribe(
      _ => {
        this.goBackToRoleListPage();
        this.store.dispatch(new LoadingRolesAction());
        this.store.dispatch(
          new QueueAlertAction({
            alertText: `New role creating succeed`,
            alertType: AlertType.SUCCESS
          })
        );
      },
      _ => {
        this.goBackToRoleListPage();
        this.store.dispatch(
          new QueueAlertAction({
            alertText: `Failed to create a new role.`,
            alertType: AlertType.ERROR
          })
        );
      }
    );
  }

  isFormValid() {
    return this.roleForm.dirty && this.roleForm.valid;
  }

  onSelectPolicy(selectedPolicies: Policy[]) {
    this.selectedPolicies = selectedPolicies;
  }

  onSelectRows(rows: SelectedRows) {
    this.selectedPolicies = rows.selected;
  }

  onSelectObjectRows(rows: SelectedRows) {
    this.selectedObjects = rows.selected;
  }

  onSelectServiceRows(rows: SelectedRows) {
    this.selectedServices = rows.selected;
  }

  private goBackToRoleListPage() {
    this.store.dispatch(new BackAction());
  }

  private buildRoleForm() {
    return this.formBuilder.group({
      roleName: ['', [Validators.required, Validators.pattern(NAME_PATTERN)]],
      roleTitle: ['', [Validators.required, Validators.pattern(DESCRIPTION_PATTERN)]],
      roleDescription: ['', [Validators.required, Validators.pattern(DESCRIPTION_PATTERN)]]
    });
  }

  private getRoleObjects() {
    return this.selectedObjects !== undefined
      ? this.selectedObjects.map(roleObject => {
          return {
            object_id: roleObject.id,
            object_role_id: '',
            object_name: roleObject.name,
            role_id: ''
          };
        })
      : [];
  }

  private getServiceRole() {
    return this.selectedServices !== undefined
      ? this.selectedServices.map(svcRole => {
          return {
            service_id: svcRole.id,
            service_role_id: '',
            service_name: svcRole.name,
            role_id: ''
          };
        })
      : [];
  }
}



// WEBPACK FOOTER //
// ./src/app/access-management/roles/role-create-new/role-create-new.component.ts